/* Shared lifecycle for the hero and manufacturing video sections. Sources remain
 * in inert templates until the selected viewport is eligible to load them. */
(() => {
  if (customElements.get('r9-video-player')) return;
  class R9VideoPlayer extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.video = this.querySelector('video[data-r9-player]');
      if (!this.video) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.mobile = matchMedia('(max-width: 749px)');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.playButton = this.querySelector('[data-r9-play]');
      this.soundButton = this.querySelector('[data-r9-sound]');
      this.status = this.querySelector('[data-r9-video-status]');
      this.near = false;
      this.visible = false;
      this.manualPaused = this.dataset.autoplay === 'false';
      this.manualMotion = false;
      this.failed = false;
      this.sequence = 0;
      this.pending = false;
      this.loaded = null;
      this.ready = this.dataset.priority !== 'hero' || document.readyState === 'complete';
      this.video.muted = true;
      this.playButton?.addEventListener('click', () => {
        if (!this.video.paused || this.pending) {
          this.manualPaused = true;
          this.stop();
        } else {
          this.manualPaused = false;
          this.manualMotion = true;
          this.failed = false;
          this.near = true;
          this.visible = true;
          this.ready = true;
          this.sync();
        }
      }, options);
      this.soundButton?.addEventListener('click', () => {
        if (!this.video.muted) {
          this.video.muted = true;
          this.updateControls();
          return;
        }
        this.manualPaused = false;
        this.manualMotion = true;
        this.failed = false;
        this.near = true;
        this.visible = true;
        this.ready = true;
        this.loadSelected();
        this.video.muted = false;
        this.sync();
      }, options);
      this.video.addEventListener('playing', () => {
        if (!this.canPlay()) { this.stop(); return; }
        this.classList.add('r9-video-ready', 'r9-hv--playing');
        if (this.status) this.status.textContent = '';
        this.updateControls();
      }, options);
      this.video.addEventListener('pause', () => this.updateControls(), options);
      this.video.addEventListener('volumechange', () => this.updateControls(), options);
      this.video.addEventListener('error', () => {
        if (!this.loaded) return;
        this.failed = true;
        this.stop();
        this.classList.remove('r9-video-ready', 'r9-hv--playing');
        if (this.status) this.status.textContent = 'Não foi possível carregar o vídeo. Toque em Reproduzir para tentar novamente.';
        console.warn('[R9 video] media error', this.id || this.dataset.r9Hero, this.video.error?.code);
        this.loaded = null;
      }, options);
      this.mobile.addEventListener('change', () => this.sync(), options);
      this.reduced.addEventListener('change', () => {
        this.manualMotion = false;
        this.stop();
        if (this.reduced.matches) this.unload();
        this.sync();
      }, options);
      document.addEventListener('visibilitychange', () => this.sync(), options);
      window.addEventListener('pagehide', () => this.stop(), options);
      window.addEventListener('pageshow', () => this.sync(), options);
      if (!this.ready) window.addEventListener('load', () => { this.ready = true; this.sync(); }, { ...options, once: true });
      if ('IntersectionObserver' in window) {
        this.nearObserver = new IntersectionObserver(entries => {
          this.near = entries[0].isIntersecting;
          this.sync();
        }, { rootMargin: '300px 0px' });
        this.viewObserver = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.15;
          this.sync();
        }, { threshold: [0, 0.15] });
        this.nearObserver.observe(this);
        this.viewObserver.observe(this);
      }
      // Without IntersectionObserver the poster and explicit play remain usable;
      // do not start downloading every below-the-fold video as a fallback.
      this.sync();
    }
    selected() {
      if (this.mobile.matches && this.dataset.mobileEnabled === 'false') return null;
      const desktop = this.querySelector('template[data-r9-source="desktop"]');
      const mobile = this.querySelector('template[data-r9-source="mobile"]');
      return this.mobile.matches ? (mobile || desktop) : (desktop || mobile);
    }
    canPlay() {
      return !!this.selected() && this.visible && !document.hidden && this.ready && !this.failed &&
        !this.manualPaused && (!this.reduced.matches || this.manualMotion);
    }
    stop() {
      this.sequence++;
      this.pending = false;
      this.video.pause();
      this.video.muted = true;
      this.updateControls();
    }
    unload() {
      this.stop();
      this.loaded = null;
      this.video.removeAttribute('src');
      this.video.replaceChildren();
      this.video.preload = 'none';
      this.video.load();
      this.classList.remove('r9-video-ready', 'r9-hv--playing');
    }
    loadSelected() {
      const selected = this.selected();
      if (!selected || selected === this.loaded) return;
      this.unload();
      this.loaded = selected;
      this.failed = false;
      this.video.append(selected.content.cloneNode(true));
      this.video.preload = 'auto';
      this.video.load();
    }
    sync() {
      const selected = this.selected();
      if (this.playButton) this.playButton.hidden = !selected;
      if (this.soundButton) this.soundButton.hidden = !selected;
      if (this.loaded && this.loaded !== selected) this.unload();
      if (!this.visible || document.hidden) {
        if (this.reduced.matches) this.manualMotion = false;
        this.stop();
      }
      if (selected && this.near && !document.hidden && this.ready && !this.failed &&
          (!this.reduced.matches || this.manualMotion)) this.loadSelected();
      if (!this.canPlay()) { this.stop(); return; }
      if (!this.loaded || this.pending || !this.video.paused) { this.updateControls(); return; }
      this.pending = true;
      const sequence = ++this.sequence;
      const result = this.video.play();
      if (result?.then) result.then(() => {
        if (sequence !== this.sequence) return;
        this.pending = false;
        if (!this.canPlay()) this.stop();
        this.updateControls();
      }).catch(error => {
        // A source switch/pause intentionally aborts an older play request.
        if (sequence !== this.sequence) return;
        this.pending = false;
        if (error.name === 'AbortError') { this.updateControls(); return; }
        this.manualPaused = true;
        this.video.muted = true;
        if (this.status) this.status.textContent = 'Toque em Reproduzir para assistir ao vídeo.';
        if (error.name !== 'NotAllowedError') console.warn('[R9 video] play failed', this.id, error);
        this.updateControls();
      });
      else { this.pending = false; this.updateControls(); }
    }
    updateControls() {
      if (this.playButton) {
        const action = this.video.paused && !this.pending ? 'Reproduzir vídeo' : 'Pausar vídeo';
        this.playButton.textContent = action;
        this.playButton.setAttribute('aria-label', action);
      }
      if (this.soundButton) {
        const action = this.video.muted ? (this.soundButton.dataset.soundLabel || 'Ativar som') : 'Silenciar vídeo';
        this.soundButton.textContent = action;
        this.soundButton.setAttribute('aria-label', action);
        this.soundButton.setAttribute('aria-pressed', String(!this.video.muted));
      }
    }
    disconnectedCallback() {
      if (!this.abort) return;
      this.abort.abort();
      this.nearObserver?.disconnect();
      this.viewObserver?.disconnect();
      this.unload();
      this.abort = null;
    }
  }
  customElements.define('r9-video-player', R9VideoPlayer);
})();
